import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { verwaistePfade } from '../../components/sifa/unterweisungModulLogic';
import { kopieAusVorlage, medienPfade, pfadImOrdner } from './vorlagenLogic';
import type {
  MatchAngaben,
  SifaMitarbeiter,
  SifaTeilnahme,
  SifaUnterweisung,
  SifaUnterweisungVorlage,
  TeilnahmeAntwort,
  UnterweisungAbschnitt,
  UnterweisungFrage,
  UnterweisungStatus,
} from './types';

/**
 * Datenbankzugriff des SiFa-Moduls „Unterweisungen" fuer den INNENDIENST —
 * Tabellen `sifa_unterweisungen`, `sifa_teilnahmen`, `sifa_mitarbeiter`
 * (supabase/sifa_unterweisungen.sql, MIGRATIONS.md Nr. 82) und die
 * angemeldeten RPCs aus Nr. 83. Die anonyme Teilnehmerseite redet NICHT
 * hierueber, sondern ueber sifaPublicService.ts (nur RPCs, keine Tabelle).
 *
 * Muster sigeEinweisungService.ts, mit denselben zwei Riegeln:
 *
 * (a) DIE LAUFENDE NUMMER WIRD NIE VOR EINEM `await` FESTGELEGT. Sie wird in
 *     der Insert-Schleife unmittelbar vor dem Schreiben geholt; der harte
 *     Riegel ist der Unique-Index (project_id, nummer), bei `23505` wird neu
 *     gelesen und erneut versucht.
 *
 * (b) GEBUCHT WIRD GENAU EINMAL. `markiereGebucht` geht ueber die RPC
 *     `sifa_teilnahme_gebucht_markieren`, die nur Zeilen mit
 *     `gebucht_am is null` anfasst und ZAEHLT, wie viele es waren. Der
 *     Aufrufer vergleicht die Zahl mit seiner Auswahl — weicht sie ab, hat
 *     ein zweiter Browser dazwischen gebucht, und die Leistungsliste wird
 *     NICHT geschrieben.
 *
 * FAIL-OPEN: solange Nr. 82/83 nicht gefahren sind, liefern die Lesewege
 * leere Listen bzw. null (42P01/PGRST205 Tabelle, PGRST202 Funktion). Eine
 * offene Migration darf die Projektansicht nicht aufreissen. Schreibwege
 * werfen — dort ist „ging nicht" die richtige Antwort.
 *
 * Alle Fehler gehen als `Error(describeError(e))` hinaus: Supabase wirft
 * schlichte Objekte, und `[object Object]` erklaert niemandem etwas.
 */

const T_UNTERWEISUNGEN = 'sifa_unterweisungen';
const T_TEILNAHMEN = 'sifa_teilnahmen';
const T_MITARBEITER = 'sifa_mitarbeiter';

/** Privater Bucket der Zertifikat-PDFs (schreibt der Server, api/unterweisung-zertifikat.ts). */
export const BUCKET_ZERTIFIKATE = 'sifa-zertifikate';
/** Privater Bucket der Abschnittsbilder aus Nr. 82 — nur noch LESEN (alte Pfade), neue Uploads gehen in BUCKET_MEDIEN. */
export const BUCKET_BILDER = 'sifa-unterweisung-bilder';
/**
 * OEFFENTLICHER Medien-Bucket der Abschnitte (Bilder + Videos, Nr. 84).
 * Oeffentlich, weil der Teilnehmer anonym ist und SQL keine URL signieren
 * kann; Lehrinhalte sind keine personenbezogenen Daten. Pfad
 * `<projectId>/<unterweisungId>/<uuid>.<ext>` — die Policies pruefen den
 * Projektordner.
 */
export const BUCKET_MEDIEN = 'sifa-unterweisung-medien';

/** Gueltigkeit einer signierten URL. Eine Stunde reicht zum Ansehen. */
const SIGNED_URL_SEKUNDEN = 3600;

/** Wie oft der Insert bei Nummernkollision erneut versucht wird. */
const MAX_NUMMER_VERSUCHE = 4;

/** Groesste zulaessige Bilddatei (10 MB) — ein Handyfoto liegt darunter, ein Scan auch. */
export const BILD_MAX_BYTES = 10 * 1024 * 1024;
/** Groesste zulaessige Videodatei (1 GB) — gleich dem Bucket-Limit aus Nr. 84. */
export const VIDEO_MAX_BYTES = 1024 * 1024 * 1024;
/** MIME-Typen, die der Bucket annimmt (Nr. 84 `allowed_mime_types`, zeichengleich). */
export const BILD_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const VIDEO_MIME = ['video/mp4', 'video/webm', 'video/quicktime'] as const;

// ─── Fehler ─────────────────────────────────────────────────────────────────

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

const errorMessage = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { message?: unknown }).message ?? '') : '';

/** 23505 = unique_violation, hier: (project_id, nummer) ist schon vergeben. */
const istNummernKollision = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Tabelle oder Funktion gibt es noch nicht (Migration Nr. 82/83 offen).
 * PGRST205/42P01 = Tabelle, PGRST202 = RPC („could not find the function").
 */
export const istFehlendesSchema = (e: unknown): boolean => {
  const code = errorCode(e);
  return (
    code === 'PGRST205' ||
    code === '42P01' ||
    code === 'PGRST202' ||
    /could not find the (function|table)/i.test(errorMessage(e))
  );
};

/**
 * Ausbau 2 (Nr. 143/144): Funktion oder Tabelle fehlt. Zusaetzlich zu
 * istFehlendesSchema 42883 (undefined_function) — so meldet Postgres eine
 * Funktion, die PostgREST zwar kennt, deren Signatur aber nicht passt.
 */
export const istFehlendeErweiterung = (e: unknown): boolean => istFehlendesSchema(e) || errorCode(e) === '42883';

/** Klartext fuer Schreibwege, solange Nr. 143/144 nicht gefahren sind. */
export const MELDUNG_ERWEITERUNG_FEHLT = 'Datenbank-Erweiterung Nr. 143/144 fehlt — diese Funktion steht erst danach zur Verfügung.';

const werfe = (e: unknown): never => {
  throw new Error(describeError(e));
};

// ─── Mapper snake → camel ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v, 0));
const boolOrNull = (v: unknown): boolean | null => (v == null ? null : v === true || v === 'true');
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Datum/Zeitstempel ohne Umrechnung — die Anzeige formatiert selbst. */
const iso = (v: unknown): string => str(v);

export const mapUnterweisung = (row: Zeile): SifaUnterweisung => ({
  id: str(row.id),
  projectId: str(row.project_id),
  nummer: num(row.nummer, 0),
  titel: str(row.titel),
  beschreibung: strOrNull(row.beschreibung),
  inhalt: arr<UnterweisungAbschnitt>(row.inhalt),
  fragen: arr<UnterweisungFrage>(row.fragen),
  bestehenProzent: num(row.bestehen_prozent, 80),
  gueltigMonate: num(row.gueltig_monate, 12),
  dauerMin: numOrNull(row.dauer_min),
  sprache: str(row.sprache) || 'de',
  status: (str(row.status) || 'entwurf') as UnterweisungStatus,
  linkToken: strOrNull(row.link_token),
  linkAktivBis: strOrNull(row.link_aktiv_bis),
  erstelltVon: strOrNull(row.erstellt_von),
  erstelltVonName: strOrNull(row.erstellt_von_name),
  companyName: strOrNull(row.company_name),
  // Ohne Nr. 141 fehlen die Spalten in der Zeile — dann null, nicht ''/0.
  vorlageId: strOrNull(row.vorlage_id) || null,
  vorlageFassung: numOrNull(row.vorlage_fassung),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapTeilnahme = (row: Zeile): SifaTeilnahme => ({
  id: str(row.id),
  unterweisungId: str(row.unterweisung_id),
  projectId: str(row.project_id),
  mitarbeiterId: strOrNull(row.mitarbeiter_id),
  vorname: strOrNull(row.vorname),
  nachname: strOrNull(row.nachname),
  geburtsdatum: strOrNull(row.geburtsdatum),
  email: strOrNull(row.email),
  begonnenAm: iso(row.begonnen_am),
  abgeschlossenAm: strOrNull(row.abgeschlossen_am),
  antworten: arr<TeilnahmeAntwort>(row.antworten),
  punkte: numOrNull(row.punkte),
  punkteMax: numOrNull(row.punkte_max),
  bestanden: boolOrNull(row.bestanden),
  unterschrift: strOrNull(row.unterschrift),
  unterschriftAm: strOrNull(row.unterschrift_am),
  unterschriftWeg: (strOrNull(row.unterschrift_weg) as SifaTeilnahme['unterschriftWeg']) ?? null,
  zertifikatNr: strOrNull(row.zertifikat_nr),
  zertifikatPfad: strOrNull(row.zertifikat_pfad),
  zertifikatGesendetAm: strOrNull(row.zertifikat_gesendet_am),
  zertifikatFehler: strOrNull(row.zertifikat_fehler),
  gueltigBis: strOrNull(row.gueltig_bis),
  gebuchtAm: strOrNull(row.gebucht_am),
  gebuchtePositionId: strOrNull(row.gebuchte_position_id),
  gebuchteMenge: numOrNull(row.gebuchte_menge),
  userAgent: strOrNull(row.user_agent),
  companyName: strOrNull(row.company_name),
  // Nr. 143; fehlen die Spalten (oder die Listen-Abfrage liest sie nicht), null.
  bestaetigtAm: strOrNull(row.bestaetigt_am),
  bestaetigungText: strOrNull(row.bestaetigung_text),
  datenschutzFassung: numOrNull(row.datenschutz_fassung),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapMitarbeiter = (row: Zeile): SifaMitarbeiter => ({
  id: str(row.id),
  projectId: str(row.project_id),
  customerId: strOrNull(row.customer_id),
  vorname: str(row.vorname),
  nachname: str(row.nachname),
  geburtsdatum: strOrNull(row.geburtsdatum),
  email: strOrNull(row.email),
  abteilung: strOrNull(row.abteilung),
  personalnummer: strOrNull(row.personalnummer),
  zusammengefuehrtIn: strOrNull(row.zusammengefuehrt_in),
  aktiv: row.aktiv !== false,
  companyName: strOrNull(row.company_name),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

// ─── Hilfen ─────────────────────────────────────────────────────────────────

/** Der Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return name?.trim() ? name.trim() : null;
  } catch {
    // Anzeige, kein Riegel — das Anlegen haengt nicht an einer Nebensache.
    return null;
  }
};

/** Angemeldeter Nutzer (id, Anzeigename) — best effort. */
const eigenerNutzer = async (): Promise<{ id: string | null; name: string | null }> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user?.id) return { id: null, name: null };
    const { data } = await supabase.from('profiles').select('first_name, last_name').eq('id', user.id).maybeSingle();
    const p = data as { first_name?: string; last_name?: string } | null;
    const name = ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || user.email || '').trim();
    return { id: user.id, name: name || null };
  } catch {
    return { id: null, name: null };
  }
};

/** Leere Zeichenkette → null (Spalten sind NULL-bar; ein '' ist dort eine Luege). */
const leerZuNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

// ═══ Unterweisungen ═══════════════════════════════════════════════════════

/**
 * Alle Unterweisungen eines Projekts, neueste Nummer zuerst. Fail-open bei
 * fehlender Tabelle. RLS antwortet mit LEERER Menge, nicht mit Fehler — wer
 * das Projekt nicht haelt, sieht „noch keine Unterweisung".
 */
export const fetchUnterweisungen = async (projectId: string): Promise<SifaUnterweisung[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(T_UNTERWEISUNGEN)
      .select('*')
      .eq('project_id', id)
      .order('nummer', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: Zeile) => mapUnterweisung(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_unterweisungen fehlt — Migration Nr. 82 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

/** Eine einzelne Unterweisung; null, wenn es sie nicht gibt (oder RLS sie verbirgt). */
export const fetchUnterweisung = async (id: string): Promise<SifaUnterweisung | null> => {
  try {
    const { data, error } = await supabase.from(T_UNTERWEISUNGEN).select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapUnterweisung(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

/**
 * Was der Aufrufer beim Anlegen setzen darf. Alles Uebrige — id, nummer,
 * status, Link, Zeitstempel — vergibt dieses Modul bzw. die Datenbank.
 * `vorlageId`/`vorlageFassung` = Herkunft aus einer Vorlage (Nr. 141).
 */
export type NeueUnterweisung = Partial<
  Pick<
    SifaUnterweisung,
    | 'titel' | 'beschreibung' | 'inhalt' | 'fragen' | 'bestehenProzent' | 'gueltigMonate' | 'dauerMin' | 'sprache'
    | 'vorlageId' | 'vorlageFassung'
  >
>;

/** Die HOECHSTE bisher vergebene Nummer + 1, frisch aus der Datenbank (Riegel (a)). */
const naechsteNummerAusDb = async (projectId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .select('nummer')
    .eq('project_id', projectId)
    .order('nummer', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const hoechste = Number((data as { nummer?: unknown } | null)?.nummer ?? 0);
  return Number.isFinite(hoechste) && hoechste > 0 ? hoechste + 1 : 1;
};

/**
 * Legt eine Unterweisung als ENTWURF an. Nummer in der Schleife unmittelbar
 * vor dem Insert; bei `23505` neu lesen und erneut versuchen; jeder andere
 * Fehler schlaegt sofort durch.
 */
export const createUnterweisung = async (projectId: string, entwurf: NeueUnterweisung): Promise<SifaUnterweisung> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine Unterweisung anlegen.');
  const titel = (entwurf.titel ?? '').trim();
  if (!titel) throw new Error('Bitte einen Titel für die Unterweisung eingeben.');

  // Herkunft NUR, wenn gesetzt: ohne Nr. 141 fehlen die Spalten, und das
  // leere Anlegen darf nicht an 42703 scheitern. Eine Fassung ohne Vorlage
  // sagt nichts und bleibt weg.
  const herkunft: Zeile = {};
  const vorlageId = (entwurf.vorlageId ?? '').trim();
  if (vorlageId) {
    herkunft.vorlage_id = vorlageId;
    if (typeof entwurf.vorlageFassung === 'number' && Number.isFinite(entwurf.vorlageFassung)) {
      herkunft.vorlage_fassung = entwurf.vorlageFassung;
    }
  }

  // Vor der Schleife: hat mit der Nummer nichts zu tun, sonst je Versuch erneut.
  const [companyName, nutzer] = await Promise.all([eigenerFirmenname(), eigenerNutzer()]);

  for (let versuch = 1; ; versuch++) {
    const nummer = await naechsteNummerAusDb(pid);
    const zeile = {
      ...herkunft,
      project_id: pid,
      nummer,
      titel,
      beschreibung: leerZuNull(entwurf.beschreibung),
      inhalt: entwurf.inhalt ?? [],
      fragen: entwurf.fragen ?? [],
      bestehen_prozent: entwurf.bestehenProzent ?? 80,
      gueltig_monate: entwurf.gueltigMonate ?? 12,
      dauer_min: entwurf.dauerMin ?? null,
      sprache: (entwurf.sprache ?? '').trim() || 'de',
      status: 'entwurf' as const,
      erstellt_von: nutzer.id,
      erstellt_von_name: nutzer.name,
      company_name: companyName,
    };

    const { data, error } = await supabase.from(T_UNTERWEISUNGEN).insert(zeile).select('*').single();
    if (!error) return mapUnterweisung(data as Zeile);
    if (!istNummernKollision(error) || versuch >= MAX_NUMMER_VERSUCHE) return werfe(error);
    // Naechste Runde: Nummer wird oben frisch geholt.
  }
};

/** Was sich an einer Unterweisung aendern laesst — Inhalt und Regeln, nicht Identitaet oder Link. */
export type UnterweisungPatch = Partial<
  Pick<
    SifaUnterweisung,
    'titel' | 'beschreibung' | 'inhalt' | 'fragen' | 'bestehenProzent' | 'gueltigMonate' | 'dauerMin' | 'sprache' | 'status'
  >
>;

const patchZuZeile = (patch: UnterweisungPatch): Zeile => {
  const z: Zeile = {};
  if (patch.titel !== undefined) z.titel = patch.titel.trim();
  if (patch.beschreibung !== undefined) z.beschreibung = leerZuNull(patch.beschreibung);
  if (patch.inhalt !== undefined) z.inhalt = patch.inhalt;
  if (patch.fragen !== undefined) z.fragen = patch.fragen;
  if (patch.bestehenProzent !== undefined) z.bestehen_prozent = patch.bestehenProzent;
  if (patch.gueltigMonate !== undefined) z.gueltig_monate = patch.gueltigMonate;
  if (patch.dauerMin !== undefined) z.dauer_min = patch.dauerMin;
  if (patch.sprache !== undefined) z.sprache = patch.sprache;
  if (patch.status !== undefined) z.status = patch.status;
  return z;
};

/**
 * Aendert eine Unterweisung im Status `entwurf` oder `aktiv`.
 *
 * Der Riegel `.in('status', ['entwurf','aktiv'])` sitzt im UPDATE selbst:
 * eine ARCHIVIERTE Unterweisung ist eingefroren — unter ihr stehen
 * Zertifikate, die sagen, worauf unterschrieben wurde. Wer sie aendern will,
 * legt eine neue an. Ein Statuswechsel nach `archiviert` ist ueber `status`
 * im Patch erlaubt (das ist das Einfrieren), zurueck aus `archiviert` nicht.
 */
export const updateUnterweisung = async (id: string, patch: UnterweisungPatch): Promise<SifaUnterweisung> => {
  const zeile = patchZuZeile(patch);
  if (typeof zeile.titel === 'string' && !zeile.titel) throw new Error('Der Titel darf nicht leer sein.');
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .update(zeile)
    .eq('id', id)
    .in('status', ['entwurf', 'aktiv'])
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapUnterweisung(data[0] as Zeile);

  const vorhanden = await fetchUnterweisung(id);
  if (!vorhanden) throw new Error('Diese Unterweisung wurde nicht gefunden (Speichern).');
  if (vorhanden.status === 'archiviert') {
    throw new Error('Diese Unterweisung ist archiviert und damit schreibgeschützt. Für Änderungen bitte eine neue anlegen.');
  }
  throw new Error('Die Unterweisung konnte nicht gespeichert werden (Speichern).');
};

/**
 * Schaltet den Kundenlink ein oder aus.
 *
 * EIN: erzeugt einen neuen `link_token` (crypto.randomUUID) und setzt den
 * Status auf `aktiv` — ein Link auf einen Entwurf haette keinen Sinn, die
 * oeffentliche RPC liefert nur aktive Kurse. `link_aktiv_bis` bleibt null
 * (unbefristet); ein Ablauf kommt ueber updateLinkAblauf.
 * AUS: loescht den Token. Der Status bleibt `aktiv` — der Kurs ist weiter
 * da, nur nicht mehr erreichbar; ein spaeteres EIN vergibt einen NEUEN
 * Token, der alte Link bleibt tot (Absicht: ein einmal verteilter Link
 * laesst sich so wirksam zurueckziehen).
 * Archivierte Kurse bekommen keinen Link.
 */
export const setzeLink = async (id: string, aktiv: boolean): Promise<SifaUnterweisung> => {
  const zeile: Zeile = aktiv
    ? { link_token: crypto.randomUUID(), link_aktiv_bis: null, status: 'aktiv' }
    : { link_token: null, link_aktiv_bis: null };
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .update(zeile)
    .eq('id', id)
    .in('status', ['entwurf', 'aktiv'])
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapUnterweisung(data[0] as Zeile);

  const vorhanden = await fetchUnterweisung(id);
  if (!vorhanden) throw new Error('Diese Unterweisung wurde nicht gefunden (Link).');
  throw new Error('Eine archivierte Unterweisung bekommt keinen Link.');
};

/** Setzt (oder loescht mit null) den Ablauf des Kundenlinks. Nur auf einer aktiven Unterweisung. */
export const updateLinkAblauf = async (id: string, aktivBisIso: string | null): Promise<SifaUnterweisung> => {
  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .update({ link_aktiv_bis: aktivBisIso, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('status', 'aktiv')
    .not('link_token', 'is', null)
    .select('*');
  if (error) return werfe(error);
  if (!data || data.length === 0) throw new Error('Der Link ist nicht aktiv — Ablauf nicht gesetzt.');
  return mapUnterweisung(data[0] as Zeile);
};

const MELDUNG_HAT_TEILNAHMEN = 'Diese Unterweisung hat Teilnahmen und lässt sich nicht löschen — bitte archivieren. Teilnahmen sind Nachweise und bleiben erhalten.';

/**
 * Loescht einen ENTWURF ohne Teilnahmen (docs/sifa-ausbau2-plan.md §0.6).
 *
 * Nachweise werden nie geloescht: eine Unterweisung, die schon einmal aktiv
 * war und Teilnahmen hat, ist auch zurueck im Entwurf nicht loeschbar (die
 * FK-Kaskade risse die Teilnahmen mit). Deshalb zuerst zaehlen; der harte
 * Riegel ist die RESTRICTIVE Delete-Policy aus Nr. 143 (`status = 'entwurf'`
 * und keine Teilnahme) — sie faengt auch eine Teilnahme, die zwischen
 * Zaehlen und Loeschen entsteht. 0 Zeilen werden nachgelesen, damit die
 * Meldung stimmt; false = gibt es nicht (mehr).
 *
 * Danach die Medien, best effort und NUR im EIGENEN Unterordner
 * `<projekt>/<id>/<datei>`: die Verweise im Inhalt plus die Liste dieses
 * Ordners (auch hochgeladene, nie gespeicherte Dateien). Ein Verweis auf die
 * Datei einer anderen Unterweisung desselben Projekts gehoert dieser und
 * bleibt; eine Kopie aus einer Vorlage zeigt auf `vorlagen/…` — diese
 * Dateien gehoeren der Vorlage und bleiben ebenso.
 */
export const deleteUnterweisungEntwurf = async (u: SifaUnterweisung): Promise<boolean> => {
  const id = (u?.id || '').trim();
  if (!id) return false;
  if (u.status !== 'entwurf') throw new Error('Nur Entwürfe lassen sich löschen — diese Unterweisung bitte archivieren.');

  if ((await zaehle(T_TEILNAHMEN, (q) => q.eq('unterweisung_id', id))) > 0) throw new Error(MELDUNG_HAT_TEILNAHMEN);

  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .delete()
    .eq('id', id)
    .eq('status', 'entwurf')
    .select('id');
  if (error) return werfe(error);
  if (!data || data.length === 0) {
    const vorhanden = await fetchUnterweisung(id);
    if (!vorhanden) return false;
    if (vorhanden.status !== 'entwurf') throw new Error('Diese Unterweisung ist inzwischen kein Entwurf mehr und lässt sich nicht löschen — bitte archivieren.');
    if ((await zaehle(T_TEILNAHMEN, (q) => q.eq('unterweisung_id', id))) > 0) throw new Error(MELDUNG_HAT_TEILNAHMEN);
    throw new Error('Die Unterweisung konnte nicht gelöscht werden — fehlt die Berechtigung für dieses Projekt?');
  }

  await raeumeProjektMedien(u);
  return true;
};

/**
 * Medien einer geloeschten Unterweisung — NUR im eigenen Unterordner
 * `<projekt>/<id>/` (pfadImOrdner mit zwei Ordnersegmenten = genau eine
 * Datei darunter). Best effort, siehe deleteUnterweisungEntwurf.
 */
const raeumeProjektMedien = async (u: SifaUnterweisung): Promise<void> => {
  const pid = (u.projectId || '').trim();
  const uid = (u.id || '').trim();
  if (!pid || !uid) return;
  const unterordner = `${pid}/${uid}`;
  const pfade = new Set(medienPfade(u.inhalt || []).filter((p) => pfadImOrdner(p, unterordner)));
  try {
    const { data, error } = await supabase.storage.from(BUCKET_MEDIEN).list(unterordner, { limit: 1000 });
    if (!error) {
      for (const datei of arr<{ name?: unknown }>(data)) {
        const pfad = `${unterordner}/${str(datei?.name)}`;
        if (pfadImOrdner(pfad, unterordner)) pfade.add(pfad);
      }
    }
  } catch {
    // Ohne Liste bleiben nur nie gespeicherte Uploads liegen — harmlos.
  }
  if (pfade.size === 0) return;
  try {
    await supabase.storage.from(BUCKET_MEDIEN).remove([...pfade]);
  } catch {
    // best effort: die Zeile ist weg, ein Rest im Bucket stoert niemanden.
  }
};

// ═══ Teilnahmen ═══════════════════════════════════════════════════════════

export type TeilnahmenFilter = { projectId: string; unterweisungId?: undefined } | { unterweisungId: string; projectId?: undefined };

/**
 * Spalten der LISTEN-Abfragen: ohne `unterschrift` (PNG-DataURL, je Zeile bis
 * zu ~100 kB) und ohne `antworten`. Eine Teilnahmenliste mit 200 Zeilen zog
 * sonst Megabytes an Unterschriftsbildern, die niemand sieht. Wer eine
 * einzelne Zeile vollstaendig braucht, nimmt fetchTeilnahmeDetail.
 */
const TEILNAHME_LISTEN_SPALTEN = [
  'id', 'unterweisung_id', 'project_id', 'mitarbeiter_id',
  'vorname', 'nachname', 'geburtsdatum', 'email',
  'begonnen_am', 'abgeschlossen_am', 'punkte', 'punkte_max', 'bestanden',
  'unterschrift_am', 'unterschrift_weg',
  'zertifikat_nr', 'zertifikat_pfad', 'zertifikat_gesendet_am', 'zertifikat_fehler', 'gueltig_bis',
  'gebucht_am', 'gebuchte_position_id', 'gebuchte_menge',
  'user_agent', 'company_name', 'created_at', 'updated_at',
].join(',');

/**
 * Teilnahmen eines Projekts ODER einer Unterweisung, neueste zuerst — OHNE
 * Unterschrift und Antworten (TEILNAHME_LISTEN_SPALTEN). Fail-open bei
 * fehlender Tabelle.
 */
export const fetchTeilnahmen = async (filter: TeilnahmenFilter): Promise<SifaTeilnahme[]> => {
  const spalte = filter.unterweisungId ? 'unterweisung_id' : 'project_id';
  const wert = ((filter.unterweisungId ?? filter.projectId) || '').trim();
  if (!wert) return [];
  try {
    const { data, error } = await supabase
      .from(T_TEILNAHMEN)
      .select(TEILNAHME_LISTEN_SPALTEN)
      .eq(spalte, wert)
      .order('begonnen_am', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: Zeile) => mapTeilnahme(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_teilnahmen fehlt — Migration Nr. 82 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

/** Eine Teilnahme VOLLSTAENDIG (mit Unterschrift und Antworten) — fuer den Einzelfall, nie fuer Listen. */
export const fetchTeilnahmeDetail = async (id: string): Promise<SifaTeilnahme | null> => {
  const tid = (id || '').trim();
  if (!tid) return null;
  try {
    const { data, error } = await supabase.from(T_TEILNAHMEN).select('*').eq('id', tid).maybeSingle();
    if (error) throw error;
    return data ? mapTeilnahme(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

/**
 * Zaehler fuer die Hub-Kacheln — `head: true` mit exaktem Count, es wird
 * keine Zeile uebertragen. Fail-open: fehlende Tabelle oder RLS = 0.
 */
const zaehle = async (tabelle: string, filter: (q: any) => any): Promise<number> => {
  try {
    const { count, error } = await filter(supabase.from(tabelle).select('id', { count: 'exact', head: true }));
    if (error) throw error;
    return typeof count === 'number' ? count : 0;
  } catch (e) {
    if (istFehlendesSchema(e)) return 0;
    return werfe(e);
  }
};

/** Bestandene Teilnahmen des Projekts (abgeschlossen UND bestanden). */
export const zaehleTeilnahmenBestanden = async (projectId: string): Promise<number> => {
  const pid = (projectId || '').trim();
  if (!pid) return 0;
  return zaehle(T_TEILNAHMEN, (q) => q.eq('project_id', pid).eq('bestanden', true).not('abgeschlossen_am', 'is', null));
};

/** Personen im Stamm ohne zusammengefuehrte Dubletten. */
export const zaehleMitarbeiter = async (projectId: string): Promise<number> => {
  const pid = (projectId || '').trim();
  if (!pid) return 0;
  return zaehle(T_MITARBEITER, (q) => q.eq('project_id', pid).is('zusammengefuehrt_in', null));
};

/**
 * Ordnet eine Teilnahme einer Person zu (oder loest die Zuordnung mit null).
 * Der Innendienst-Weg fuer `mehrdeutig` aus findeMitarbeiter.
 */
export const ordneTeilnahmeZu = async (teilnahmeId: string, mitarbeiterId: string | null): Promise<SifaTeilnahme> => {
  const { data, error } = await supabase
    .from(T_TEILNAHMEN)
    .update({ mitarbeiter_id: mitarbeiterId, updated_at: new Date().toISOString() })
    .eq('id', teilnahmeId)
    .select('*');
  if (error) return werfe(error);
  if (!data || data.length === 0) throw new Error('Diese Teilnahme wurde nicht gefunden (Zuordnen).');
  return mapTeilnahme(data[0] as Zeile);
};

/**
 * Vermerkt die Buchung auf einer LV-Position — GENAU EINMAL je Teilnahme
 * (RPC `sifa_teilnahme_gebucht_markieren`, Riegel `gebucht_am is null`).
 *
 * Rueckgabe: die ids, die DIESER Aufruf tatsaechlich gebucht hat (die RPC
 * liefert `uuid[]`). Fehlt eine uebergebene id darin, war sie schon gebucht —
 * der Aufrufer schreibt dann NUR die gemeldeten in die Leistungsliste. Kein
 * Fehler dafuer: der zweite Klick ist der Normalfall, nicht die Ausnahme.
 * Eine Zahl (aeltere SQL-Fassung) wird NICHT als Erfolg gedeutet: ohne ids
 * weiss der Client nicht, welche Zeilen er gutschreiben darf.
 */
export const markiereGebucht = async (ids: string[], positionId: string, menge: number): Promise<string[]> => {
  const liste = (ids ?? []).map((x) => (x || '').trim()).filter(Boolean);
  if (liste.length === 0) return [];
  const { data, error } = await supabase.rpc('sifa_teilnahme_gebucht_markieren', {
    p_ids: liste,
    p_position_id: positionId,
    p_menge: menge,
  });
  if (error) return werfe(error);
  if (!Array.isArray(data)) return [];
  const erlaubt = new Set(liste);
  return data.map((x) => str(x).trim()).filter((x) => x && erlaubt.has(x));
};

/** Signierte URL zum Zertifikat-PDF (Bucket privat: Name, Geburtsdatum, Unterschrift). */
export const signedUrlZertifikat = async (pfad: string): Promise<string> => {
  const p = (pfad || '').trim();
  if (!p) throw new Error('Zu dieser Teilnahme liegt noch kein Zertifikat vor.');
  const { data, error } = await supabase.storage.from(BUCKET_ZERTIFIKATE).createSignedUrl(p, SIGNED_URL_SEKUNDEN);
  if (error) return werfe(error);
  if (!data?.signedUrl) throw new Error('Das Zertifikat konnte nicht geladen werden.');
  return data.signedUrl;
};

// ═══ Mitarbeiter ══════════════════════════════════════════════════════════

/**
 * Mitarbeiterstamm eines Projekts, alphabetisch (Nachname, Vorname) —
 * EINSCHLIESSLICH zusammengefuehrter Zeilen (`zusammengefuehrtIn`), damit die
 * Liste sagen kann „aufgegangen in …". Wer nur die lebenden will, filtert.
 * Fail-open bei fehlender Tabelle.
 */
export const fetchMitarbeiter = async (projectId: string): Promise<SifaMitarbeiter[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(T_MITARBEITER)
      .select('*')
      .eq('project_id', id)
      .order('nachname', { ascending: true })
      .order('vorname', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r: Zeile) => mapMitarbeiter(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_mitarbeiter fehlt — Migration Nr. 82 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

export type MitarbeiterEingabe = Partial<
  Pick<SifaMitarbeiter, 'id' | 'customerId' | 'geburtsdatum' | 'email' | 'abteilung' | 'personalnummer'>
> & { vorname: string; nachname: string };

/**
 * Legt eine Person an (ohne `id`) oder aendert sie (mit `id`). Name ist
 * Pflicht; leere Felder werden NULL, nicht ''. Zusammenfuehren geht NICHT
 * hierueber (eigene RPC, zusammenfuehren).
 */
export const upsertMitarbeiter = async (projectId: string, m: MitarbeiterEingabe): Promise<SifaMitarbeiter> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich kein Mitarbeiter anlegen.');
  const vorname = (m.vorname ?? '').trim();
  const nachname = (m.nachname ?? '').trim();
  if (!vorname || !nachname) throw new Error('Vorname und Nachname sind Pflicht.');

  const felder: Zeile = {
    vorname,
    nachname,
    geburtsdatum: leerZuNull(m.geburtsdatum),
    email: leerZuNull(m.email),
    abteilung: leerZuNull(m.abteilung),
    personalnummer: leerZuNull(m.personalnummer),
    updated_at: new Date().toISOString(),
  };
  if (m.customerId !== undefined) felder.customer_id = leerZuNull(m.customerId);

  const id = (m.id ?? '').trim();
  if (id) {
    const { data, error } = await supabase
      .from(T_MITARBEITER)
      .update(felder)
      .eq('id', id)
      .eq('project_id', pid)
      .select('*');
    if (error) return werfe(error);
    if (!data || data.length === 0) throw new Error('Dieser Mitarbeiter wurde nicht gefunden (Speichern).');
    return mapMitarbeiter(data[0] as Zeile);
  }

  const companyName = await eigenerFirmenname();
  const { data, error } = await supabase
    .from(T_MITARBEITER)
    .insert({ ...felder, project_id: pid, company_name: companyName })
    .select('*')
    .single();
  if (error) return werfe(error);
  return mapMitarbeiter(data as Zeile);
};

/**
 * Serverseitiger Abgleich „3 von 4" (RPC `sifa_mitarbeiter_match`) — dieselbe
 * Regel, die beim Abschluss einer Teilnahme entscheidet. Fuer den Dialog,
 * wenn er die Antwort der Datenbank und nicht die TS-Vorschau will.
 * Fail-open bei fehlender RPC.
 */
export const matchMitarbeiterServer = async (projectId: string, angaben: MatchAngaben): Promise<SifaMitarbeiter[]> => {
  const pid = (projectId || '').trim();
  if (!pid) return [];
  try {
    const { data, error } = await supabase.rpc('sifa_mitarbeiter_match', {
      p_project_id: pid,
      p_vorname: leerZuNull(angaben.vorname),
      p_nachname: leerZuNull(angaben.nachname),
      p_geburtsdatum: leerZuNull(angaben.geburtsdatum),
      p_email: leerZuNull(angaben.email),
    });
    if (error) throw error;
    return arr<Zeile>(data).map(mapMitarbeiter);
  } catch (e) {
    if (istFehlendesSchema(e)) return [];
    return werfe(e);
  }
};

/**
 * Fuehrt `quelle` in `ziel` zusammen (RPC `sifa_mitarbeiter_zusammenfuehren`):
 * Teilnahmen der Quelle wandern ans Ziel, die Quelle bekommt
 * `zusammengefuehrt_in`. Rueckgabe: Anzahl umgehaengter Teilnahmen.
 */
export const zusammenfuehren = async (quelle: string, ziel: string): Promise<number> => {
  const q = (quelle || '').trim();
  const z = (ziel || '').trim();
  if (!q || !z) throw new Error('Zum Zusammenführen braucht es Quelle und Ziel.');
  if (q === z) throw new Error('Eine Person lässt sich nicht mit sich selbst zusammenführen.');
  const { data, error } = await supabase.rpc('sifa_mitarbeiter_zusammenfuehren', { p_quelle: q, p_ziel: z });
  if (error) return werfe(error);
  const n = Number(data);
  return Number.isFinite(n) ? n : 0;
};

// ═══ Bilder ═══════════════════════════════════════════════════════════════

/** Dateiendung aus dem Namen, klein, ohne Punkt. Ohne erkennbare Endung: 'jpg'. */
const endungAus = (fileName: string): string => {
  const treffer = /\.([a-zA-Z0-9]{1,8})$/.exec((fileName || '').trim());
  return treffer ? treffer[1].toLowerCase() : 'jpg';
};

export type MedienArt = 'bild' | 'video';

/** Bild oder Video — nach dem MIME-Typ, zur Not nach der Endung (Windows meldet .mov gern ohne Typ). */
export const medienArt = (file: Pick<File, 'type' | 'name'> | Blob): MedienArt | null => {
  const type = (file.type || '').toLowerCase();
  if ((BILD_MIME as readonly string[]).includes(type)) return 'bild';
  if ((VIDEO_MIME as readonly string[]).includes(type)) return 'video';
  const endung = endungAus((file as File).name || '');
  if (['jpg', 'jpeg', 'png', 'webp'].includes(endung)) return 'bild';
  if (['mp4', 'webm', 'mov'].includes(endung)) return 'video';
  return null;
};

/** Der MIME-Typ, den der Bucket fuer diese Datei annimmt (Nr. 84 prueft ihn). */
const medienMime = (file: Pick<File, 'type' | 'name'> | Blob, art: MedienArt): string => {
  const type = (file.type || '').toLowerCase();
  if (type && ([...BILD_MIME, ...VIDEO_MIME] as string[]).includes(type)) return type;
  const endung = endungAus((file as File).name || '');
  if (art === 'video') return endung === 'webm' ? 'video/webm' : endung === 'mov' ? 'video/quicktime' : 'video/mp4';
  return endung === 'png' ? 'image/png' : endung === 'webp' ? 'image/webp' : 'image/jpeg';
};

/**
 * Upload per XMLHttpRequest gegen die Storage-REST-API — einzig deshalb,
 * weil supabase-js keinen Upload-Fortschritt liefert und ein 150-MB-Video
 * ohne Balken wie ein Haenger aussieht. Gleicher Endpunkt, gleiche Header
 * (`x-upsert: false`) wie der SDK-Upload; ohne Browser-XHR (Test, Node)
 * oder ohne Sitzung faellt der Aufrufer auf den SDK-Weg zurueck.
 */
const uploadMitFortschritt = async (
  bucket: string,
  path: string,
  file: File | Blob,
  contentType: string,
  onFortschritt: (geladen: number, gesamt: number) => void,
): Promise<boolean> => {
  if (typeof XMLHttpRequest === 'undefined') return false;
  const client = supabase as { supabaseUrl?: string; supabaseKey?: string; auth: { getSession: () => Promise<{ data: { session: { access_token?: string } | null } }> } };
  const baseUrl = (client.supabaseUrl || '').replace(/\/+$/, '');
  const apiKey = client.supabaseKey || '';
  if (!baseUrl || !apiKey) return false;
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return false;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const ziel = `${baseUrl}/storage/v1/object/${bucket}/${path.split('/').map(encodeURIComponent).join('/')}`;
    xhr.open('POST', ziel, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('apikey', apiKey);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('cache-control', 'max-age=3600');
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onFortschritt(ev.loaded, ev.total);
    };
    xhr.onerror = () => reject(new Error('Der Upload ist abgebrochen (Netzwerk).'));
    xhr.onabort = () => reject(new Error('Der Upload wurde abgebrochen.'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onFortschritt(file.size, file.size);
        resolve();
        return;
      }
      let meldung = '';
      try {
        const antwort = JSON.parse(xhr.responseText || '{}') as { message?: string; error?: string };
        meldung = antwort.message || antwort.error || '';
      } catch {
        meldung = '';
      }
      if (xhr.status === 413) {
        meldung = 'Die Datei ist für den Speicher zu groß. Das projektweite Upload-Limit in Supabase (Storage → Settings) muss mindestens 1 GB betragen.';
      }
      reject(new Error(meldung || `Upload fehlgeschlagen (HTTP ${xhr.status}).`));
    };
    xhr.send(file);
  });
  return true;
};

/**
 * Dateiname im Medien-Bucket: `<uuid>.<endung>`. Ohne erkennbare Endung im
 * Namen nach der Art (mp4 bzw. jpg) — ein namenloses Video als `.jpg`
 * abzulegen, haette den Bucket-Ordner nur verwirrt.
 */
export const medienDateiname = (file: File | Blob): string => {
  const name = ((file as File).name || '').trim();
  const endung = /\.[a-zA-Z0-9]{1,8}$/.test(name) ? endungAus(name) : medienArt(file) === 'video' ? 'mp4' : 'jpg';
  return `${crypto.randomUUID()}.${endung}`;
};

/**
 * Upload-Kern fuer den OEFFENTLICHEN Medien-Bucket (Nr. 84) — gemeinsam fuer
 * Projekt- und Vorlagenordner, damit Typ-/Groessenpruefung und
 * Fortschrittsweg nur einmal existieren. Der Aufrufer baut den Pfad und
 * verantwortet den Ordner; die Storage-Policy prueft ihn ein zweites Mal.
 *
 * `upsert: false` — ein zweiter Upload ersetzt den VERWEIS im Abschnitt, nie
 * eine Datei, auf die ein anderer Stand noch zeigt. Bilder bis 10 MB, Videos
 * bis 1 GB (Bucket-Limit; das projektweite Limit in Supabase muss
 * mitziehen, sonst 413). `onFortschritt(geladen, gesamt)` in Bytes — nur mit
 * Browser-XHR, der SDK-Weg meldet nichts. Ein heruntergeladener Blob hat
 * keinen Namen: dann entscheidet die Endung des Zielpfads ueber Art und
 * MIME-Typ.
 */
export const ladeMediumHoch = async (
  path: string,
  file: File | Blob,
  onFortschritt?: (geladen: number, gesamt: number) => void,
): Promise<{ path: string; art: MedienArt }> => {
  const ziel = (path || '').trim();
  if (!ziel) throw new Error('Ohne Ablagepfad lässt sich keine Datei hochladen.');
  if (!file || file.size <= 0) throw new Error('Die Datei ist leer.');
  const quelle = { type: file.type || '', name: (file as File).name || ziel.split('/').pop() || '' };
  const art = medienArt(quelle);
  if (!art) throw new Error('Bitte ein Bild (JPG, PNG, WebP) oder ein Video (MP4, WebM, MOV) wählen.');
  if (art === 'bild' && file.size > BILD_MAX_BYTES) throw new Error('Das Bild ist zu groß (höchstens 10 MB).');
  if (art === 'video' && file.size > VIDEO_MAX_BYTES) throw new Error('Das Video ist zu groß (höchstens 1 GB).');

  const contentType = medienMime(quelle, art);
  const melde = onFortschritt ?? (() => undefined);

  try {
    const perXhr = await uploadMitFortschritt(BUCKET_MEDIEN, ziel, file, contentType, melde);
    if (!perXhr) {
      const { error } = await supabase.storage
        .from(BUCKET_MEDIEN)
        .upload(ziel, file, { upsert: false, contentType });
      if (error) return werfe(error);
      melde(file.size, file.size);
    }
  } catch (e) {
    return werfe(e);
  }
  return { path: ziel, art };
};

/**
 * Laedt ein Bild oder Video eines Abschnitts in den Projektordner des
 * Medien-Buckets: `<projekt>/<unterweisung>/<uuid>.<endung>` (Regeln siehe
 * ladeMediumHoch). Der Aufrufer schreibt den Pfad in `bildPfad` bzw.
 * `videoPfad`.
 */
export const uploadUnterweisungMedium = async (
  projectId: string,
  unterweisungId: string,
  file: File | Blob,
  onFortschritt?: (geladen: number, gesamt: number) => void,
): Promise<{ path: string; art: MedienArt }> => {
  const pid = (projectId || '').trim();
  const uid = (unterweisungId || '').trim();
  if (!pid || !uid) throw new Error('Ohne Projekt und Unterweisung lässt sich keine Datei ablegen.');
  if (!file || file.size <= 0) throw new Error('Die Datei ist leer.');
  return ladeMediumHoch(`${pid}/${uid}/${medienDateiname(file)}`, file, onFortschritt);
};

/**
 * Public-URL eines Pfads im Medien-Bucket — synchron, ohne Netz: Supabase
 * setzt sie aus Projekt-URL, Bucket und Pfad zusammen. Ein alter Pfad aus dem
 * privaten Bucket (Nr. 82) ergibt eine URL, die 400/404 liefert; die Anzeige
 * blendet ein nicht ladbares Bild aus.
 */
export const medienUrl = (pfad: string | null | undefined): string | null => {
  const p = (pfad || '').trim();
  if (!p) return null;
  try {
    const url = supabase.storage.from(BUCKET_MEDIEN).getPublicUrl(p).data?.publicUrl;
    return url || null;
  } catch {
    return null;
  }
};

/** Entfernt ein Medium aus dem Bucket (Abschnitt geloescht, Video ersetzt). Fehler werden gemeldet, nicht verschluckt. */
export const loescheUnterweisungMedium = async (pfad: string): Promise<void> => {
  const p = (pfad || '').trim();
  if (!p) return;
  const { error } = await supabase.storage.from(BUCKET_MEDIEN).remove([p]);
  if (error) return werfe(error);
};

/**
 * Bild eines Abschnitts hochladen — seit Nr. 84 in den Medien-Bucket (vorher
 * privater Bucket `sifa-unterweisung-bilder`). Nimmt NUR Bilder; fuer Videos
 * `uploadUnterweisungMedium`.
 */
export const uploadUnterweisungBild = async (
  projectId: string,
  unterweisungId: string,
  file: File | Blob,
  onFortschritt?: (geladen: number, gesamt: number) => void,
): Promise<{ path: string }> => {
  if (medienArt(file) !== 'bild') throw new Error('Bitte eine Bilddatei wählen (JPG, PNG, WebP).');
  const { path } = await uploadUnterweisungMedium(projectId, unterweisungId, file, onFortschritt);
  return { path };
};

/** Signierte URL eines Abschnittsbilds aus dem ALTEN privaten Bucket (Pfade aus Nr. 82, zwei Segmente). */
export const signedUrlUnterweisungBild = async (pfad: string): Promise<string> => {
  const p = (pfad || '').trim();
  if (!p) throw new Error('Kein Bild hinterlegt.');
  const { data, error } = await supabase.storage.from(BUCKET_BILDER).createSignedUrl(p, SIGNED_URL_SEKUNDEN);
  if (error) return werfe(error);
  if (!data?.signedUrl) throw new Error('Das Bild konnte nicht geladen werden.');
  return data.signedUrl;
};

// ═══ Aus Vorlage (Nr. 141) ════════════════════════════════════════════════

/** Ohne Freigabe sind die Dateien der Vorlage noch loeschbar — eine Kopie darauf koennte ins Leere zeigen. */
const pruefeVorlageUebernehmbar = (v: SifaUnterweisungVorlage | null | undefined): SifaUnterweisungVorlage => {
  if (!v || !(v.id || '').trim()) throw new Error('Keine Vorlage gewählt.');
  if (v.status !== 'aktiv') throw new Error('Nur eine freigegebene Vorlage lässt sich übernehmen.');
  return v;
};

/**
 * Neue Kunden-Unterweisung aus einer freigegebenen Vorlage: ein ENTWURF mit
 * Inhalt und Herkunft (`vorlage_id`/`vorlage_fassung`). Ueber
 * createUnterweisung, damit Nummer und 23505-Riegel dieselben bleiben. Die
 * Medien werden NICHT kopiert — die Kopie zeigt auf `vorlagen/<id>/…`.
 */
export const createUnterweisungAusVorlage = async (projectId: string, v: SifaUnterweisungVorlage): Promise<SifaUnterweisung> =>
  createUnterweisung(projectId, kopieAusVorlage(pruefeVorlageUebernehmbar(v)));

/**
 * Die Kundenkopie steht schon auf der Fassung der Vorlage oder weiter — kein
 * Fehler im eigentlichen Sinn (meist war ein zweiter Browser schneller),
 * deshalb ein eigener Typ: die Oberflaeche meldet es als Hinweis und
 * uebernimmt `stand`, den frisch gelesenen Datensatz.
 */
export class FassungBereitsAktuellError extends Error {
  readonly stand: SifaUnterweisung;
  constructor(stand: SifaUnterweisung) {
    super(`Diese Unterweisung steht bereits auf Fassung ${stand.vorlageFassung ?? '?'}.`);
    this.name = 'FassungBereitsAktuellError';
    this.stand = stand;
  }
}

/**
 * Uebernimmt den Stand der Vorlage in eine bestehende Kundenkopie: Titel,
 * Beschreibung, Inhalt, Fragen, Grenze, Gueltigkeit, Dauer, Sprache und
 * `vorlage_fassung`. Nummer, Link und Teilnahmen bleiben — Aushang und QR
 * funktionieren weiter.
 *
 * Drei Riegel im UPDATE selbst:
 *  - `.in('status', ['entwurf','aktiv'])` — eine archivierte Kopie bleibt
 *    eingefroren;
 *  - `.eq('vorlage_id', v.id)` — nur eine Kopie DIESER Vorlage;
 *  - `vorlage_fassung is null or vorlage_fassung < v.fassung` — zwei
 *    Uebernahmen mit verschieden altem Vorlagenstand setzen die Kopie nie auf
 *    eine aeltere Fassung zurueck; die spaetere, aeltere trifft 0 Zeilen.
 * 0 Zeilen werden nachgelesen: schon aktuell → FassungBereitsAktuellError,
 * archiviert und nicht gefunden → Klartext.
 *
 * Danach werden Medien, auf die die Kopie nicht mehr zeigt, geloescht — NUR
 * im Projektordner (eigene Uploads der Kundenfassung), nie unter
 * `vorlagen/`. Best effort: ein Rest im Bucket stoert niemanden, ein
 * Fehler hier darf die gelungene Uebernahme nicht als gescheitert melden.
 */
export const aktualisiereAusVorlage = async (u: SifaUnterweisung, v: SifaUnterweisungVorlage): Promise<SifaUnterweisung> => {
  const id = (u?.id || '').trim();
  if (!id) throw new Error('Keine Unterweisung gewählt.');
  const vorlage = pruefeVorlageUebernehmbar(v);
  if ((u.vorlageId ?? '').trim() !== vorlage.id) throw new Error('Diese Unterweisung stammt nicht aus der gewählten Vorlage.');
  // Die Zahl geht unten in einen PostgREST-Filtertext — nur eine echte ganze Zahl darf hinein.
  const fassung = vorlage.fassung;
  if (!Number.isInteger(fassung) || fassung < 1) throw new Error('Die Fassung der Vorlage ist unbekannt. Bitte neu laden.');

  const zeile = patchZuZeile(kopieAusVorlage(vorlage));
  if (typeof zeile.titel === 'string' && !zeile.titel) throw new Error('Die Vorlage hat keinen Titel.');
  zeile.vorlage_fassung = fassung;
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(T_UNTERWEISUNGEN)
    .update(zeile)
    .eq('id', id)
    .eq('vorlage_id', vorlage.id)
    .in('status', ['entwurf', 'aktiv'])
    .or(`vorlage_fassung.is.null,vorlage_fassung.lt.${fassung}`)
    .select('*');
  if (error) return werfe(error);
  if (!data || data.length === 0) {
    const vorhanden = await fetchUnterweisung(id);
    if (!vorhanden) throw new Error('Diese Unterweisung wurde nicht gefunden (Übernehmen).');
    if (vorhanden.vorlageId === vorlage.id && typeof vorhanden.vorlageFassung === 'number' && vorhanden.vorlageFassung >= fassung) {
      throw new FassungBereitsAktuellError(vorhanden);
    }
    if (vorhanden.status === 'archiviert') {
      throw new Error('Diese Unterweisung ist archiviert und bleibt auf ihrem Stand. Für die neue Fassung bitte eine neue Unterweisung aus der Vorlage anlegen.');
    }
    if (vorhanden.vorlageId !== vorlage.id) throw new Error('Diese Unterweisung stammt nicht mehr aus dieser Vorlage.');
    throw new Error('Die neue Fassung konnte nicht übernommen werden.');
  }

  const neu = mapUnterweisung(data[0] as Zeile);
  for (const pfad of verwaistePfade(u, neu, (u.projectId || '').trim())) {
    await loescheUnterweisungMedium(pfad).catch(() => undefined);
  }
  return neu;
};

// ═══ Kachel-Aktivitaet ════════════════════════════════════════════════════

export interface SifaAktivitaet {
  /** max(updated_at) ueber Unterweisungen UND Teilnahmen des Projekts. */
  unterweisung: string | null;
  /** max(updated_at) des Mitarbeiterstamms. */
  mitarbeiter: string | null;
}

/** Juengstes updated_at einer Tabelle je Projekt; null bei leer oder fehlender Tabelle. */
const maxUpdatedAt = async (tabelle: string, projectId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from(tabelle)
      .select('updated_at')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return strOrNull((data as { updated_at?: unknown } | null)?.updated_at);
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

const juengeres = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
};

/**
 * Letzte Aktivitaet je Kachel (ModuleActivityKey 'unterweisung' |
 * 'mitarbeiter', services/moduleActivity.ts). Drei kleine Abfragen statt
 * einer Sicht — fail-open, solange die Tabellen fehlen.
 */
export const letzteAktivitaet = async (projectId: string): Promise<SifaAktivitaet> => {
  const id = (projectId || '').trim();
  if (!id) return { unterweisung: null, mitarbeiter: null };
  const [u, t, m] = await Promise.all([
    maxUpdatedAt(T_UNTERWEISUNGEN, id),
    maxUpdatedAt(T_TEILNAHMEN, id),
    maxUpdatedAt(T_MITARBEITER, id),
  ]);
  return { unterweisung: juengeres(u, t), mitarbeiter: m };
};
